import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import localApiAdapter from '../../core/services/localApiAdapter';
import { Calculator, Users, DollarSign, TrendingUp, Award, FileText, Filter, ChevronDown, Upload, Download, FileSpreadsheet, Calendar, CheckCircle, XCircle, Table2, Printer, X, Check, Eye } from 'lucide-react';
import { useAttendanceData } from '../../shared/hooks/useAttendanceData';
import { useDepartmentData } from '../../shared/hooks/useDepartmentData';
import { CustomSelect } from '../../shared/components/custom';
import { slugifyExportName } from '../../shared/utils/exportFilename';
import toast from 'react-hot-toast';
import BulletinSalaire from './components/salaries/BulletinSalaire';
import {
  calculerSalaireCotisableImposable,
  calculerSalaireImposableNonCotisable,
  calculerSalaireNonCotisableNonImposable,
  calculerFraisMissionAvancesRetenues,
  calculerPrejudice,
  calculerRecapitulatifCotisations
} from '../../shared/utils/salaryCalculator';
import { mapSalariesConfigToGosiSettings } from './utils/payrollSaudiFormat';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../../shared/styles/GPSTrackingDatePicker.css';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QawaemTableStickyActionsFooterTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useSalairesTableSelection,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import { useSalairesConfig } from './useSalairesConfig';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';

const NS = 'modules.salaires.pages.journalCaissePage';
const ALL_DEPARTMENTS = '__all__';
const MONTH_KEYS = [
  'month_january', 'month_february', 'month_march', 'month_april',
  'month_may', 'month_june', 'month_july', 'month_august',
  'month_september', 'month_october', 'month_november', 'month_december',
];

const JournalCaissePage = () => {
  const { t, i18n } = useTranslation();
  const p = useCallback((key, opts) => t(`${NS}.${key}`, opts), [t]);
  const fmtNum = useCallback((val) => formatBrandMoney(parseFloat(val) || 0, false), []);
  const fmtMoney = useCallback((val) => formatBrandMoney(parseFloat(val) || 0), []);

  const now = new Date();
  const [period, setPeriod] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [departement, setDepartement] = useState(ALL_DEPARTMENTS);
  const [isImportDropdownOpen, setIsImportDropdownOpen] = useState(false);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  
  // Modal states
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [selectedEmployeesForAction, setSelectedEmployeesForAction] = useState([]);
  const [showBulletinPreview, setShowBulletinPreview] = useState(false);
  const [currentBulletinEmployee, setCurrentBulletinEmployee] = useState(null);
  const { employees: hrEmployees, loading: employeesLoading } = useSalairesEmployeeOptions();
  const salariesConfig = useSalairesConfig();
  const gosiSettings = useMemo(
    () => mapSalariesConfigToGosiSettings(salariesConfig),
    [salariesConfig],
  );
  const employees = useMemo(
    () => hrEmployees.map((emp) => ({
      ...emp,
      mat: emp.mat || emp.employeeId || String(emp.id),
      nom: emp.nom || emp.lastName || '',
      prenom: emp.prenom || emp.firstName || '',
      salaireBase: emp.salaireBase ?? emp.salary ?? emp.basic_salary ?? 0,
      departement: emp.departement || emp.affectation || emp.department || '',
    })),
    [hrEmployees],
  );

  // Refs for dropdowns
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    if (!showValidationModal && !showGenerationModal && !showBulletinPreview) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showValidationModal, showGenerationModal, showBulletinPreview]);

  const currentYear = period.year;
  const currentMonthNum = period.month;
  const periodLabel = `${p(MONTH_KEYS[currentMonthNum - 1])} ${currentYear}`;
  const periodOptions = useMemo(() => {
    const year = 2026;
    return MONTH_KEYS.map((key, idx) => ({
      value: `${idx + 1}-${year}`,
      label: `${p(key)} ${year}`,
    }));
  }, [p]);

  // Fetch employees from HR API (same source as Employees page) — via useSalairesEmployeeOptions

  const { departments, loading: departmentsLoading } = useDepartmentData();
  const { attendanceData: pointageData, loading: pointageLoading } = useAttendanceData(currentMonthNum, currentYear);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setIsImportDropdownOpen(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setIsExportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter employees by department
  const filteredEmployees = useMemo(() => {
    if (departement === ALL_DEPARTMENTS) return employees;
    return employees.filter(emp => emp.departement === departement);
  }, [employees, departement]);

  // Calculate journal data for each employee
  const journalData = useMemo(() => {
    return filteredEmployees.map((employee, idx) => {
      const employeePointage = pointageData.find(p => p.employeeId === employee.id || p.employeeId === employee.mat) || {
        nbrJoursMois: 30,
        nbrJoursOuvrables: 22,
        nbrJoursTravailles: 22,
        nbrJoursSupplementaires: 0,
        nbrHeuresSupplementaires: 0,
        nbrJoursAbsence: 0,
        nbrJoursConges: 0
      };

      // Calculate all salary components
      const salaireBase = employee.salaireBase || 0;
      
      const salaireCotisableImposable = calculerSalaireCotisableImposable(
        employee,
        employeePointage.nbrJoursTravailles || 22,
        employeePointage.nbrHeuresSupplementaires || 0,
        employeePointage.nbrJoursConges || 0
      );

      const salaireImposableNonCotisable = calculerSalaireImposableNonCotisable(
        employee,
        employeePointage.nbrJoursTravailles || 22
      );

      const salaireNonCotisableNonImposable = calculerSalaireNonCotisableNonImposable(
        employee,
        employeePointage.nbrJoursTravailles || 22
      );

      const fraisMission = calculerFraisMissionAvancesRetenues(employee, employeePointage);
      const prejudice = calculerPrejudice(employee);

      const totalSCI = salaireCotisableImposable.totalSCI || 0;
      const totalSCNI = 0;
      const salairePoste = salaireBase;
      const totalSINC = salaireImposableNonCotisable.totalSINC || 0;
      const totalSNCNI = salaireNonCotisableNonImposable.totalSNCNI || 0;

      const salaireBrut = totalSCI + totalSCNI + salairePoste + totalSINC;

      const recap = calculerRecapitulatifCotisations(employee, salaireBrut, {});

      const totalFraisMission = fraisMission.totalFraisMission || 0;
      const totalAvances = fraisMission.totalAvances || 0;
      const totalRetenues = fraisMission.totalRetenues || 0;
      const totalPrejudice = prejudice.totalPrejudice || 0;

      const netAPayer = recap.netComptable + totalSNCNI + totalFraisMission - totalAvances - totalRetenues - totalPrejudice;

      return {
        id: employee.id,
        rowNum: idx + 1,
        mat: employee.mat,
        nom: employee.nom,
        prenom: employee.prenom,
        nbrJoursTravail: employeePointage.nbrJoursTravailles || 0,
        nbrJoursWeekend: 8,
        nbrPtsWeekend: 0,
        nbrJoursFerie: 1,
        nbrPtsFerie: 0,
        nbrJoursConge: employeePointage.nbrJoursConges || 0,
        nbrJoursAbsence: employeePointage.nbrJoursAbsence || 0,
        nbrJoursObservation: 0,
        salaireBase,
        salaireCotisable: totalSCI,
        salaireCotisableNonImposable: totalSCNI,
        salairePoste,
        salaireImposableNonCotisable: totalSINC,
        salaireBrut,
        retenueGOSI: recap.retenueGOSISalarie ?? recap.retenueSSSalarie ?? 0,
        retenueSANED: recap.retenueSANEDSalarie ?? 0,
        provisionEOSB: recap.provisionEOSB ?? recap.cacobatCongesPayes ?? 0,
        salaireImposable: recap.salaireImposable || 0,
        rappelIndemnite: totalSNCNI,
        fraisMission: totalFraisMission,
        avance: totalAvances,
        retenuePret: totalRetenues,
        prejudice: totalPrejudice,
        netAPayer
      };
    });
  }, [filteredEmployees, pointageData]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(journalData);

  // Calculate stats
  const stats = useMemo(() => {
    return {
      totalEmployees: journalData.length,
      totalPayroll: journalData.reduce((sum, emp) => sum + emp.netAPayer, 0),
      avgSalary: journalData.length > 0 
        ? Math.round(journalData.reduce((sum, emp) => sum + emp.netAPayer, 0) / journalData.length)
        : 0,
      totalBonuses: journalData.reduce((sum, emp) => sum + emp.salaireCotisable, 0),
      totalAllowances: journalData.reduce((sum, emp) => sum + emp.salaireImposableNonCotisable, 0)
    };
  }, [journalData]);

  // Build department options
  const departmentOptions = [
    { value: ALL_DEPARTMENTS, label: t('common.all_departments'), icon: <Filter className="h-4 w-4 text-white/70" /> },
    ...(departments || [])
      .filter(dept => dept.status === 'active')
      .map(dept => ({
        value: dept.name,
        label: dept.name
      }))
  ];

  // Loading state - MUST be after all hooks
  if (employeesLoading || departmentsLoading || pointageLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-white text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  // Export functions
  const exportToExcel = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(journalData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, p('title'));
      const exportBase = slugifyExportName(p('export_filename', { period: periodLabel.replace(/ /g, '_') }));
      XLSX.writeFile(wb, `${exportBase}.xlsx`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert(p('export_excel_error'));
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a3');

      const headers = [
        p('col_num'), p('col_mat'), p('col_last_name'), p('col_first_name'),
        p('col_work_days'), p('col_weekend_days'), p('col_weekend_pts'), p('col_holiday_days'),
        p('col_holiday_pts'), p('col_leave_days'), p('col_absence_days'), p('col_observation_days'),
        p('col_base_salary'), p('col_cotisable'), p('col_scni'), p('col_post_salary'),
        p('col_sinc'), p('col_gross'), p('col_ss_deduction'), p('col_paid_leave'),
        p('col_gosi_deduction'), p('col_saned_deduction'), p('col_eosb_provision'), p('col_indemnity_recall'), p('col_mission_expenses'),
        p('col_advance'), p('col_deductions'), p('col_prejudice'), p('col_net_pay'),
      ];
      
      const data = journalData.map(emp => [
        emp.rowNum,
        emp.mat,
        emp.nom,
        emp.prenom,
        emp.nbrJoursTravail,
        emp.nbrJoursWeekend,
        emp.nbrPtsWeekend,
        emp.nbrJoursFerie,
        emp.nbrPtsFerie,
        emp.nbrJoursConge,
        emp.nbrJoursAbsence,
        emp.nbrJoursObservation,
        fmtNum(emp.salaireBase),
        fmtNum(emp.salaireCotisable),
        fmtNum(emp.salaireCotisableNonImposable),
        fmtNum(emp.salairePoste),
        fmtNum(emp.salaireImposableNonCotisable),
        fmtNum(emp.salaireBrut),
        fmtNum(emp.retenueGOSI),
        fmtNum(emp.retenueSANED),
        fmtNum(emp.provisionEOSB),
        fmtNum(emp.rappelIndemnite),
        fmtNum(emp.fraisMission),
        fmtNum(emp.avance),
        fmtNum(emp.retenuePret),
        fmtNum(emp.prejudice),
        fmtNum(emp.netAPayer)
      ]);
      
      doc.setFontSize(18);
      doc.text(p('export_pdf_title'), 14, 22);
      doc.setFontSize(12);
      doc.text(p('export_pdf_month', { period: periodLabel }), 14, 30);
      
      doc.autoTable({
        head: [headers],
        body: data,
        startY: 40,
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fontSize: 8,
          fillColor: [80, 80, 80],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        }
      });
      
      const exportBase = slugifyExportName(p('export_filename', { period: periodLabel.replace(/ /g, '_') }));
      doc.save(`${exportBase}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      alert(p('export_pdf_error'));
    }
  };

  const generateBulletinData = (employee, empJournalData) => {
    const monthNum = currentMonthNum;
    const yearStr = String(currentYear);
    const firstDay = `01/${String(monthNum).padStart(2, '0')}/${yearStr}`;
    const lastDay = new Date(parseInt(yearStr), monthNum, 0).getDate();
    const lastDayStr = `${lastDay}/${String(monthNum).padStart(2, '0')}/${yearStr}`;

    // Find employee's pointage data
    const empPointage = pointageData.find(p => p.employeeId === employee.id || p.employeeId === employee.mat) || {};

    const employeeDataForBulletin = {
      matricule: employee.mat || '',
      nom: employee.nom || '',
      prenom: employee.prenom || '',
      dateNaissance: employee.dateNaissance || '',
      adresse: employee.adresse || '',
      fonction: employee.fonction || employee.poste || '',
      dateEntree: employee.dateEmbauche || '',
      numeroSS: employee.numeroSecuriteSociale || '',
      numeroCCP: employee.numeroCCP || employee.numeroCompte || '',
      situationFamiliale: employee.situationFamiliale || p('marital_single'),
      enfants: employee.nombreEnfants || 0,
      grille: employee.grille || p('na')
    };

    const salaryDataForBulletin = {
      periode: {
        du: firstDay,
        au: lastDayStr,
        joursOuvrables: empPointage.nbrJoursOuvrables || 22,
        joursTravailles: empPointage.nbrJoursTravailles || 22,
        joursAbsences: empPointage.nbrJoursAbsence || 0,
        heuresTravaillees: (empPointage.nbrJoursTravailles || 22) * 8
      },
      gains: [
        { code: 'SB', libelle: p('bulletin_base_salary'), base: '', taux: '', nombreOuMontant: empJournalData.salaireBase, imposable: true, cotisable: true },
        { code: 'SCI', libelle: p('bulletin_sci'), base: '', taux: '', nombreOuMontant: empJournalData.salaireCotisable, imposable: true, cotisable: true },
        { code: 'SINC', libelle: p('bulletin_sinc'), base: '', taux: '', nombreOuMontant: empJournalData.salaireImposableNonCotisable, imposable: true, cotisable: false }
      ],
      retenues: [
        { code: '5000', libelle: p('bulletin_gosi'), base: empJournalData.salaireBase, taux: gosiSettings.gosiEmployeeRate ?? 9.75, nombreOuMontant: empJournalData.retenueGOSI, imposable: false, cotisable: false },
        { code: 'SANED', libelle: p('bulletin_saned'), base: empJournalData.salaireBase, taux: gosiSettings.sanedEmployeeRate ?? 1, nombreOuMontant: empJournalData.retenueSANED, imposable: false, cotisable: false }
      ],
      cotisationsEmployeur: [
        { libelle: p('bulletin_gosi_employer'), taux: gosiSettings.gosiEmployerRate ?? 12.75, montant: empJournalData.salaireBase * ((gosiSettings.gosiEmployerRate ?? 12.75) / 100) },
        { libelle: p('bulletin_saned_employer'), taux: gosiSettings.sanedEmployerRate ?? 1, montant: empJournalData.salaireBase * ((gosiSettings.sanedEmployerRate ?? 1) / 100) },
        { libelle: p('bulletin_eosb'), taux: 0, montant: empJournalData.provisionEOSB || 0 }
      ],
      totaux: {
        brut: empJournalData.salaireBrut,
        cotisable: empJournalData.salaireCotisable,
        imposable: empJournalData.salaireImposable,
        retenues: empJournalData.retenueGOSI + empJournalData.retenueSANED,
        net: empJournalData.netAPayer
      }
    };
    
    return { employeeDataForBulletin, salaryDataForBulletin };
  };

  // Handle individual bulletin generation
  const handleGenerateSingleBulletin = (employee, journalEntry) => {
    if (!employee || !journalEntry) {
      toast.error(p('toast_salary_data_not_found'));
      return;
    }
    
    setCurrentBulletinEmployee({ employee, empJournalData: journalEntry });
    setShowBulletinPreview(true);
  };

  const qawaemBaseColumns = useMemo(() => [
    { key: 'num', label: p('col_num'), required: true },
    { key: 'mat', label: p('col_mat'), required: true },
    { key: 'last_name', label: p('col_last_name') },
    { key: 'first_name', label: p('col_first_name') },
    { key: 'work_days', label: p('col_work_days') },
    { key: 'weekend_days', label: p('col_weekend_days') },
    { key: 'weekend_pts', label: p('col_weekend_pts') },
    { key: 'holiday_days', label: p('col_holiday_days') },
    { key: 'holiday_pts', label: p('col_holiday_pts') },
    { key: 'leave_days', label: p('col_leave_days') },
    { key: 'absence_days', label: p('col_absence_days') },
    { key: 'observation_days', label: p('col_observation_days') },
    { key: 'base_salary', label: p('col_base_salary') },
    { key: 'cotisable', label: p('col_cotisable') },
    { key: 'scni', label: p('col_scni') },
    { key: 'post_salary', label: p('col_post_salary') },
    { key: 'sinc', label: p('col_sinc') },
    { key: 'gross', label: p('col_gross') },
    { key: 'gosi_deduction', label: p('col_gosi_deduction') },
    { key: 'saned_deduction', label: p('col_saned_deduction') },
    { key: 'eosb_provision', label: p('col_eosb_provision') },
    { key: 'indemnity_recall', label: p('col_indemnity_recall') },
    { key: 'mission_expenses', label: p('col_mission_expenses') },
    { key: 'advance', label: p('col_advance') },
    { key: 'deductions', label: p('col_deductions') },
    { key: 'prejudice', label: p('col_prejudice') },
    { key: 'net_pay', label: p('col_net_pay'), required: true },
  ], [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('common.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: -1 }),
    [qawaemBaseColumns, showCol],
  );
  const footerIdentityColSpan = useMemo(() => {
    const identityKeys = ['num', 'mat', 'last_name', 'first_name'];
    return Math.max(1, identityKeys.filter((key) => showCol(key)).length);
  }, [showCol]);

  return (
    <div className={`space-y-6`}>
      {/* En-tête */}
      <div>
        <h2 className="text-3xl font-bold text-white flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-400" />
          {p('title')}
        </h2>
        <p className="text-white/60 mt-2">{p('period_label', { period: periodLabel })}</p>
      </div>

      {/* 5 Stat Cards with Glow Effect */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total_employees'), value: stats.totalEmployees, icon: Users, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_payroll_mass'), value: fmtMoney(stats.totalPayroll), icon: DollarSign, color: 'from-green-500 to-green-600' },
          { title: p('stat_avg_salary'), value: fmtMoney(stats.avgSalary), icon: TrendingUp, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_total_bonuses'), value: fmtMoney(stats.totalBonuses), icon: Award, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_total_allowances'), value: fmtMoney(stats.totalAllowances), icon: FileText, color: 'from-pink-500 to-pink-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 min-h-[140px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-xl font-bold text-white force-white mt-2 break-words">{stat.value}</p>
                </div>
                <div className="relative flex-shrink-0 ml-3">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Main Content Container */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                />
                {showCol('num') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-blue-100 uppercase tracking-wider bg-blue-700/30">{p('col_num')}</th> : null}
                {showCol('mat') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-blue-100 uppercase tracking-wider bg-blue-700/30">{p('col_mat')}</th> : null}
                {showCol('last_name') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-blue-100 uppercase tracking-wider bg-blue-700/30">{p('col_last_name')}</th> : null}
                {showCol('first_name') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-blue-100 uppercase tracking-wider bg-blue-700/30">{p('col_first_name')}</th> : null}
                {showCol('work_days') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_work_days')}</th> : null}
                {showCol('weekend_days') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_weekend_days')}</th> : null}
                {showCol('weekend_pts') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_weekend_pts')}</th> : null}
                {showCol('holiday_days') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_holiday_days')}</th> : null}
                {showCol('holiday_pts') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_holiday_pts')}</th> : null}
                {showCol('leave_days') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_leave_days')}</th> : null}
                {showCol('absence_days') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_absence_days')}</th> : null}
                {showCol('observation_days') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-cyan-100 uppercase tracking-wider bg-cyan-700/30">{p('col_observation_days')}</th> : null}
                {showCol('base_salary') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-green-100 uppercase tracking-wider bg-green-700/30">{p('col_base_salary')}</th> : null}
                {showCol('cotisable') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-green-100 uppercase tracking-wider bg-green-700/30">{p('col_cotisable')}</th> : null}
                {showCol('scni') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-green-100 uppercase tracking-wider bg-green-700/30">{p('col_scni')}</th> : null}
                {showCol('post_salary') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-green-100 uppercase tracking-wider bg-green-700/30">{p('col_post_salary')}</th> : null}
                {showCol('sinc') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-green-100 uppercase tracking-wider bg-green-700/30">{p('col_sinc')}</th> : null}
                {showCol('gross') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-green-100 uppercase tracking-wider bg-green-700/30">{p('col_gross')}</th> : null}
                {showCol('gosi_deduction') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-red-100 uppercase tracking-wider bg-red-700/30">{p('col_gosi_deduction')}</th> : null}
                {showCol('saned_deduction') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-red-100 uppercase tracking-wider bg-red-700/30">{p('col_saned_deduction')}</th> : null}
                {showCol('eosb_provision') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-red-100 uppercase tracking-wider bg-red-700/30">{p('col_eosb_provision')}</th> : null}
                {showCol('indemnity_recall') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-pink-100 uppercase tracking-wider bg-pink-700/30">{p('col_indemnity_recall')}</th> : null}
                {showCol('mission_expenses') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-pink-100 uppercase tracking-wider bg-pink-700/30">{p('col_mission_expenses')}</th> : null}
                {showCol('advance') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-pink-100 uppercase tracking-wider bg-pink-700/30">{p('col_advance')}</th> : null}
                {showCol('deductions') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-pink-100 uppercase tracking-wider bg-pink-700/30">{p('col_deductions')}</th> : null}
                {showCol('prejudice') ? <th className="px-3 py-3.5 text-left text-xs font-semibold text-pink-100 uppercase tracking-wider bg-pink-700/30">{p('col_prejudice')}</th> : null}
                {showCol('net_pay') ? <th className="px-3 py-3.5 text-left text-xs font-bold text-emerald-100 uppercase tracking-wider bg-emerald-700/30">{p('col_net_pay')}</th> : null}
                <QawaemTableHeaderActionsCell
                  label={t('common.actions')}
                  columns={qawaemColumns}
                  onToggle={handleToggleColumn}
                  visibleCount={visibleColumnCount}
                  totalCount={qawaemColumns.length}
                  thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {journalData.map((emp) => (
                <tr 
                  key={emp.id}
                  className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(emp.id) ? 'bg-white/10' : ''}`}
                >
                  <QawaemTableSelectRowCell
                    checked={selectedRecords.includes(emp.id)}
                    onChange={() => handleSelectRecord(emp.id)}
                  />
                  {showCol('num') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.rowNum}</td> : null}
                  {showCol('mat') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.mat}</td> : null}
                  {showCol('last_name') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nom}</td> : null}
                  {showCol('first_name') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.prenom}</td> : null}
                  {showCol('work_days') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrJoursTravail}</td> : null}
                  {showCol('weekend_days') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrJoursWeekend}</td> : null}
                  {showCol('weekend_pts') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrPtsWeekend}</td> : null}
                  {showCol('holiday_days') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrJoursFerie}</td> : null}
                  {showCol('holiday_pts') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrPtsFerie}</td> : null}
                  {showCol('leave_days') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrJoursConge}</td> : null}
                  {showCol('absence_days') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrJoursAbsence}</td> : null}
                  {showCol('observation_days') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{emp.nbrJoursObservation}</td> : null}
                  {showCol('base_salary') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.salaireBase)}</td> : null}
                  {showCol('cotisable') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.salaireCotisable)}</td> : null}
                  {showCol('scni') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.salaireCotisableNonImposable)}</td> : null}
                  {showCol('post_salary') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.salairePoste)}</td> : null}
                  {showCol('sinc') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.salaireImposableNonCotisable)}</td> : null}
                  {showCol('gross') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.salaireBrut)}</td> : null}
                  {showCol('gosi_deduction') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.retenueGOSI)}</td> : null}
                  {showCol('saned_deduction') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.retenueSANED)}</td> : null}
                  {showCol('eosb_provision') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.provisionEOSB)}</td> : null}
                  {showCol('indemnity_recall') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.rappelIndemnite)}</td> : null}
                  {showCol('mission_expenses') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.fraisMission)}</td> : null}
                  {showCol('advance') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.avance)}</td> : null}
                  {showCol('deductions') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.retenuePret)}</td> : null}
                  {showCol('prejudice') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white">{fmtMoney(emp.prejudice)}</td> : null}
                  {showCol('net_pay') ? <td className="px-3 py-4 whitespace-nowrap text-sm text-white font-bold">{fmtMoney(emp.netAPayer)}</td> : null}
                  <QawaemTableStickyActionsTd>
                    <QawaemErpRowActions
                      onView={() => {
                        const employee = filteredEmployees.find((e) => e.id === emp.id);
                        if (employee) {
                          handleGenerateSingleBulletin(employee, emp);
                        } else {
                          toast.error(p('toast_employee_not_found'));
                        }
                      }}
                      onEdit={() => {
                        toast.success(p('toast_sheet_validated', { name: `${emp.nom} ${emp.prenom}` }));
                      }}
                      viewTitle={p('generate_bulletin_title')}
                      editTitle={p('validate_sheet_title')}
                    />
                  </QawaemTableStickyActionsTd>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-white/5 border-t border-white/20">
                <td colSpan={footerIdentityColSpan} className="px-3 py-3 text-right text-sm text-white font-medium">{p('totals')}</td>
                {showCol('work_days') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrJoursTravail, 0)}</td> : null}
                {showCol('weekend_days') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrJoursWeekend, 0)}</td> : null}
                {showCol('weekend_pts') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrPtsWeekend, 0)}</td> : null}
                {showCol('holiday_days') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrJoursFerie, 0)}</td> : null}
                {showCol('holiday_pts') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrPtsFerie, 0)}</td> : null}
                {showCol('leave_days') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrJoursConge, 0)}</td> : null}
                {showCol('absence_days') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrJoursAbsence, 0)}</td> : null}
                {showCol('observation_days') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{journalData.reduce((sum, emp) => sum + emp.nbrJoursObservation, 0)}</td> : null}
                {showCol('base_salary') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.salaireBase, 0))}</td> : null}
                {showCol('cotisable') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.salaireCotisable, 0))}</td> : null}
                {showCol('scni') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.salaireCotisableNonImposable, 0))}</td> : null}
                {showCol('post_salary') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.salairePoste, 0))}</td> : null}
                {showCol('sinc') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.salaireImposableNonCotisable, 0))}</td> : null}
                {showCol('gross') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.salaireBrut, 0))}</td> : null}
                {showCol('gosi_deduction') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.retenueGOSI, 0))}</td> : null}
                {showCol('saned_deduction') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.retenueSANED, 0))}</td> : null}
                {showCol('eosb_provision') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.provisionEOSB, 0))}</td> : null}
                {showCol('indemnity_recall') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.rappelIndemnite, 0))}</td> : null}
                {showCol('mission_expenses') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.fraisMission, 0))}</td> : null}
                {showCol('advance') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.avance, 0))}</td> : null}
                {showCol('deductions') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.retenuePret, 0))}</td> : null}
                {showCol('prejudice') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.prejudice, 0))}</td> : null}
                {showCol('net_pay') ? <td className="px-3 py-3 whitespace-nowrap text-sm text-white font-bold">{fmtMoney(journalData.reduce((sum, emp) => sum + emp.netAPayer, 0))}</td> : null}
                <QawaemTableStickyActionsFooterTd />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Validation Modal */}
      {showValidationModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" role="presentation">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl w-full max-w-2xl border border-white/20 shadow-2xl overflow-hidden"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-b border-white/10 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{p('modal_validate_title')}</h3>
                    <p className="text-white/60 text-sm mt-1">{p('period_label', { period: periodLabel })}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    setSelectedEmployeesForAction([]);
                  }}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <p className="text-white/70 mb-4">
                {p('modal_validate_desc', { period: periodLabel })}
              </p>

              {/* Employee List */}
              <div className="space-y-2">
                {filteredEmployees.length === 0 ? (
                  <p className="text-white/50 text-center py-8">{p('modal_no_employees')}</p>
                ) : (
                  filteredEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className="flex items-center p-3 rounded-xl hover:bg-white/5 transition-colors border border-white/10"
                    >
                      <input
                        type="checkbox"
                        id={`val-employee-${employee.id}`}
                        checked={selectedEmployeesForAction.includes(employee.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmployeesForAction([...selectedEmployeesForAction, employee.id]);
                          } else {
                            setSelectedEmployeesForAction(selectedEmployeesForAction.filter(id => id !== employee.id));
                          }
                        }}
                        className="h-4 w-4 rounded border-white/30 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 bg-white/10"
                      />
                      <label
                        htmlFor={`val-employee-${employee.id}`}
                        className="ml-3 text-white cursor-pointer flex-1 flex items-center justify-between"
                      >
                        <span className="font-medium">{employee.nom} {employee.prenom}</span>
                        <span className="text-white/50 text-sm">{employee.mat}</span>
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-white/5 border-t border-white/10 p-6 flex justify-between items-center">
              <button
                onClick={() => {
                  if (selectedEmployeesForAction.length === filteredEmployees.length) {
                    setSelectedEmployeesForAction([]);
                  } else {
                    setSelectedEmployeesForAction(filteredEmployees.map(emp => emp.id));
                  }
                }}
                className="text-white/70 hover:text-white text-sm font-medium transition-colors"
              >
                {selectedEmployeesForAction.length === filteredEmployees.length ? p('deselect_all') : p('select_all')}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    setSelectedEmployeesForAction([]);
                  }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    if (selectedEmployeesForAction.length === 0) {
                      toast.error(p('toast_select_employee'));
                      return;
                    }
                    toast.success(p('toast_sheets_validated', { count: selectedEmployeesForAction.length }));
                    setShowValidationModal(false);
                    setSelectedEmployeesForAction([]);
                  }}
                  disabled={selectedEmployeesForAction.length === 0}
                  className={`px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium flex items-center gap-2 transition-all ${
                    selectedEmployeesForAction.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-500/30'
                  }`}
                >
                  <Check className="h-5 w-5" />
                  {p('validate_count', { count: selectedEmployeesForAction.length })}
                </button>
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Generation Modal */}
      {showGenerationModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" role="presentation">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl w-full max-w-2xl border border-white/20 shadow-2xl overflow-hidden"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-b border-white/10 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{p('modal_generate_title')}</h3>
                    <p className="text-white/60 text-sm mt-1">{p('period_label', { period: periodLabel })}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowGenerationModal(false);
                    setSelectedEmployeesForAction([]);
                  }}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <p className="text-white/70 mb-4">
                {p('modal_generate_desc', { period: periodLabel })}
              </p>

              {/* Employee List */}
              <div className="space-y-2">
                {filteredEmployees.length === 0 ? (
                  <p className="text-white/50 text-center py-8">{p('modal_no_employees')}</p>
                ) : (
                  filteredEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className="flex items-center p-3 rounded-xl hover:bg-white/5 transition-colors border border-white/10"
                    >
                      <input
                        type="checkbox"
                        id={`gen-employee-${employee.id}`}
                        checked={selectedEmployeesForAction.includes(employee.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmployeesForAction([...selectedEmployeesForAction, employee.id]);
                          } else {
                            setSelectedEmployeesForAction(selectedEmployeesForAction.filter(id => id !== employee.id));
                          }
                        }}
                        className="h-4 w-4 rounded border-white/30 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 bg-white/10"
                      />
                      <label
                        htmlFor={`gen-employee-${employee.id}`}
                        className="ml-3 text-white cursor-pointer flex-1 flex items-center justify-between"
                      >
                        <span className="font-medium">{employee.nom} {employee.prenom}</span>
                        <span className="text-white/50 text-sm">{employee.mat}</span>
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-white/5 border-t border-white/10 p-6 flex justify-between items-center">
              <button
                onClick={() => {
                  if (selectedEmployeesForAction.length === filteredEmployees.length) {
                    setSelectedEmployeesForAction([]);
                  } else {
                    setSelectedEmployeesForAction(filteredEmployees.map(emp => emp.id));
                  }
                }}
                className="text-white/70 hover:text-white text-sm font-medium transition-colors"
              >
                {selectedEmployeesForAction.length === filteredEmployees.length ? p('deselect_all') : p('select_all')}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowGenerationModal(false);
                    setSelectedEmployeesForAction([]);
                  }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    if (selectedEmployeesForAction.length === 0) {
                      toast.error(p('toast_select_employee'));
                      return;
                    }
                    const firstEmpId = selectedEmployeesForAction[0];
                    const employee = filteredEmployees.find(e => e.id === firstEmpId);
                    const journalEntry = journalData.find(j => j.id === firstEmpId);
                    
                    if (employee && journalEntry) {
                      handleGenerateSingleBulletin(employee, journalEntry);
                      toast.success(p('toast_bulletin_generated', { name: `${employee.nom} ${employee.prenom}` }));
                    } else {
                      toast.error(p('toast_bulletin_error'));
                    }
                    
                    setShowGenerationModal(false);
                    setSelectedEmployeesForAction([]);
                  }}
                  disabled={selectedEmployeesForAction.length === 0}
                  className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium flex items-center gap-2 transition-all ${
                    selectedEmployeesForAction.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/30'
                  }`}
                >
                  <FileText className="h-5 w-5" />
                  {p('generate_count', { count: selectedEmployeesForAction.length })}
                </button>
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Bulletin Preview Modal */}
      {showBulletinPreview && currentBulletinEmployee && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4 overflow-y-auto" role="presentation">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl w-full max-w-5xl border border-white/20 shadow-2xl overflow-hidden my-8"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-b border-white/10 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{p('modal_bulletin_title')}</h3>
                    <p className="text-white/60 text-sm mt-1">
                      {currentBulletinEmployee.employee.nom} {currentBulletinEmployee.employee.prenom} - {periodLabel}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowBulletinPreview(false);
                    setCurrentBulletinEmployee(null);
                  }}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body - Bulletin Preview */}
            <div className="p-6 bg-white/5 max-h-[70vh] overflow-y-auto">
              {(() => {
                const { employeeDataForBulletin, salaryDataForBulletin } = generateBulletinData(
                  currentBulletinEmployee.employee,
                  currentBulletinEmployee.empJournalData
                );
                
                return (
                  <BulletinSalaire
                    employeeData={employeeDataForBulletin}
                    salaryData={salaryDataForBulletin}
                    month={currentMonthNum}
                    year={currentYear}
                  />
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="bg-white/5 border-t border-white/10 p-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulletinPreview(false);
                  setCurrentBulletinEmployee(null);
                }}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                {t('common.close')}
              </button>
              <button
                onClick={() => {
                  window.print();
                  toast.success(p('toast_print_ready'));
                }}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium flex items-center gap-2 hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-500/30 transition-all"
              >
                <Printer className="h-5 w-5" />
                {p('print_pdf')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default JournalCaissePage;


